import { resolveHref } from "./resolve-href";
import type { PaletteContext, PaletteItem } from "./types";

/**
 * What selecting an item should do.
 *
 * `closes` and `recordsRecent` are part of the outcome rather than left to the
 * caller, because getting them wrong is invisible: an early `close()` on a page
 * push shipped a Compare action that dismissed the palette and threw away the
 * page it had just opened, and every unit test still passed. Making the rule a
 * value makes it assertable.
 */
export type SelectionOutcome =
  | { kind: "blocked"; closes: false; recordsRecent: false }
  | { kind: "push"; page: string; closes: false; recordsRecent: false }
  // `recordsRecent` is the one outcome field that is not fixed by the kind:
  // proposal rows navigate but are never remembered, because Recents resolve
  // against the static item list and a venue is not in it.
  | { kind: "navigate"; href: string; closes: true; recordsRecent: boolean }
  | { kind: "action"; closes: true; recordsRecent: true }
  | { kind: "unresolvable"; closes: false; recordsRecent: false };

/**
 * Decides what selecting an item does, without doing any of it.
 *
 * @param item - The selected item.
 * @param context - The palette's resolved context.
 * @returns The outcome, including whether the palette closes.
 */
export function resolveSelection(item: PaletteItem, context: PaletteContext): SelectionOutcome {
  if (item.requires && !item.requires(context)) {
    return { kind: "blocked", closes: false, recordsRecent: false };
  }

  // Scope is checked before the run kind, not after. Guarding only `navigate`
  // let an event-scoped item whose run is a page push skip the check entirely:
  // Compare opened its selection page with no event and listed nothing, instead
  // of asking which event first.
  if (item.scope === "event" && !context.eventId) {
    return { kind: "unresolvable", closes: false, recordsRecent: false };
  }

  if (item.run.kind === "page") {
    return { kind: "push", page: item.run.page, closes: false, recordsRecent: false };
  }

  if (item.run.kind === "action") {
    return { kind: "action", closes: true, recordsRecent: true };
  }

  const href = resolveHref(item.run.href, context.eventId);
  // Belt and braces: a global-scoped href could still carry the placeholder.
  // Staying open is the safe outcome, since closing would look like it ran.
  if (!href) return { kind: "unresolvable", closes: false, recordsRecent: false };

  // Proposal rows are never recorded. Recents resolve against the static item
  // list and a venue is not in it, so four proposal selections would fill all
  // four slots with ids that resolve to nothing and the Recent group would
  // quietly empty itself. Decided here rather than at a call site for the reason
  // in this module's docstring: both callers navigate, and only one of them
  // would have remembered to carry the exception.
  return { kind: "navigate", href, closes: true, recordsRecent: item.group !== "proposals" };
}
